import { useMutation, useQuery } from "@apollo/client/react";
import { FC, useState } from "react";

import { CircularProgress, Fab, Grid, Stack, Tooltip, Typography, styled, useTheme } from "@mui/material";
import { PencilIcon } from "@phosphor-icons/react/dist/csr/Pencil";
import { toast } from "react-toastify";
import { HistoryTable, IfPermission, NewDraftPrompt, hasPermission } from "../../../components";
import { DataLoader, EmptyContainer, LoadingContainer, SuccessContainer } from "../../../components/DataLoader";
import { DraftsTable } from "../../../components/DraftTable";
import { InfiniteScrollContainer } from "../../../components/InfiniteScrollContainer";
import { CountDisplay } from "../../../components/ui/CountDisplay";
import { EmptyContainer as Empty } from "../../../components/ui/EmptyContainer";
import { SkeletonRow, SkeletonTable } from "../../../components/ui/skeletons";
import Permissions from "../../../constants/permissions";
import useAuth from "../../../context/UserContext";
import { BRANCH_CHANGELIST_MUTATION, DRAFT_LIST_QUERY } from "../../../graphql";
import {
  BranchChangelistMutation,
  BranchChangelistMutationVariables,
  Changelist,
  PublishedDraftDocument,
  PublishedDraftQuery,
  PublishedDraftQueryVariables,
} from "../../../graphql/types";
import { useNavigator } from "../../../lib/Hooks";
import { toLocalAndUtcDates } from "../../../lib/Util";

const PAGE_SIZE = 20;

const DraftCreate = styled(Fab, {
  shouldForwardProp: (prop) => prop !== "applyTransition",
})<{
  applyTransition?: boolean;
}>(({ applyTransition, theme }) => ({
  margin: 0,
  top: "auto",
  right: 20,
  transition: "bottom 0.3s linear",
  bottom: applyTransition ? 70 : 20,
  left: "auto",
  position: "fixed",
  "&:disabled": {
    color: theme.palette.surfaceLight10.main,
    backgroundColor: theme.palette.secondary30.main,
  },
}));

interface PublishedDraftsProps {
  loading?: boolean;
  published?: PublishedDraftQuery["changelist"];
}

const PublishedDraft = ({ published, loading }: PublishedDraftsProps) => (
  <Grid container spacing={2}>
    <Grid size={12}>
      <Typography variant="heading.five" sx={{ fontWeight: 500 }}>
        Published Version
      </Typography>
    </Grid>
    <Grid size={12}>{loading ? <SkeletonRow /> : <HistoryTable drafts={published ? [published] : null} />}</Grid>
  </Grid>
);

interface IActiveDrafts {
  hasPublished: boolean;
  setApplyTransition?: React.Dispatch<React.SetStateAction<boolean>>;
}

const ActiveDrafts: FC<IActiveDrafts> = ({ hasPublished, setApplyTransition }) => {
  const { user } = useAuth();
  return (
    <InfiniteScrollContainer<Changelist>
      first={PAGE_SIZE}
      className={"drafts-browser"}
      query={DRAFT_LIST_QUERY}
      variables={{ archived: false, first: PAGE_SIZE }}
      nodeName="drafts"
      data-cy="infinite-scroll"
      setApplyTransition={setApplyTransition}
    >
      {({ loading, error, results, hasResults, isLoadingMore }) => {
        const draftCount = results ? results.length : 0;
        return (
          <Grid container spacing={2}>
            <Grid size={12}>
              <Stack direction="row" sx={{ justifyContent: "space-between" }}>
                <Typography variant="heading.five" sx={{ fontWeight: 500 }}>
                  Active Drafts
                </Typography>
              </Stack>
            </Grid>

            <Grid size={12}>
              <DataLoader loading={loading && !isLoadingMore} error={error} success={hasResults}>
                <LoadingContainer>
                  <SkeletonTable rows={10} />
                </LoadingContainer>
                <EmptyContainer>
                  {hasPermission(Permissions.DevelopScripts, user) && !hasPublished ? (
                    <NewDraftPrompt />
                  ) : (
                    <Empty title="No Drafts" />
                  )}
                </EmptyContainer>
                <SuccessContainer>
                  <DraftsTable drafts={results as Changelist[]} hasPublished={hasPublished} />
                  {isLoadingMore && <SkeletonRow />}
                  <CountDisplay count={draftCount} entity="draft" testLabel="draft-count-top" sticky={false} />
                </SuccessContainer>
              </DataLoader>
            </Grid>
          </Grid>
        );
      }}
    </InfiniteScrollContainer>
  );
};

const DraftBrowser = () => {
  const { loading, data } = useQuery<PublishedDraftQuery, PublishedDraftQueryVariables>(PublishedDraftDocument, {
    errorPolicy: "all",
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });
  const theme = useTheme();
  const navigator = useNavigator();
  const utcnow = toLocalAndUtcDates(new Date().toISOString());
  const [result, mutationObject] = useMutation<BranchChangelistMutation, BranchChangelistMutationVariables>(
    BRANCH_CHANGELIST_MUTATION,
    {
      refetchQueries: ["drafts"],
      onError: () => {
        toast.error("There was a problem creating your Draft.");
      },
    }
  );
  const [applyTransition, setApplyTransition] = useState<boolean>(false);
  const createDraft = () => {
    if (!data?.changelist) return;
    result({
      variables: {
        description: `Version created on ${utcnow.browser} from ${data.changelist.description}`,
        changelist_id: data.changelist.id,
      },
    });
  };

  if (mutationObject.data) {
    const newChangelistId = mutationObject.data.branch_changelist?.changelist?.id;
    if (newChangelistId) {
      navigator.goto(["scripts", "editor", newChangelistId], {}, { addReferrer: true });
      toast.success("Draft Created");
    }
  }

  return (
    <Grid container spacing={4} className="DraftBrowser" data-cy="draft-browser">
      <Grid size={12}>
        {data?.changelist && (
          <IfPermission permission={Permissions.DevelopScripts}>
            <Tooltip id="tooltip" title="Create a new Draft from the published version." placement="right">
              <DraftCreate
                color="primary"
                size="medium"
                data-cy="create-draft"
                onClick={createDraft}
                disabled={mutationObject.loading}
                applyTransition={applyTransition}
              >
                {mutationObject.loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <PencilIcon color={theme.palette.text10.main} />
                )}
              </DraftCreate>
            </Tooltip>
          </IfPermission>
        )}
        <PublishedDraft loading={loading} published={data?.changelist || undefined} />
      </Grid>
      <Grid size={12}>
        <ActiveDrafts setApplyTransition={setApplyTransition} hasPublished={Boolean(data?.changelist)} />
      </Grid>
    </Grid>
  );
};

export { DraftBrowser };
